import React from "react";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const services = [
  {
    logo_images: "../assets/icons/terraform/service-1.png",
    title: "Module Development",
    description:
      "Design reusable Terraform modules with standardized variables, outputs, testing, and documentation to accelerate infrastructure delivery while enforcing governance.",
  },
  {
    logo_images: "../assets/icons/terraform/service-2.png",
    title: "Multi-Cloud Automation",
    description:
      "Provision infrastructure consistently across AWS, Azure, Google Cloud, and on-prem with provider-agnostic Terraform code, remote state, and policy enforcement.",
  },
  {
    logo_images: "../assets/icons/terraform/service-3.png",
    title: "State Management",
    description:
      "Secure remote state backends (S3, Azure Storage, GCS, Terraform Cloud) with locking, versioning, encryption, and disaster recovery strategies.",
  },
  {
    logo_images: "../assets/icons/terraform/service-4.png",
    title: "CI/CD Integrations",
    description:
      "Embed Terraform plan/apply workflows into GitHub Actions, GitLab CI, Jenkins, and Azure DevOps with automated validation, policy checks, and approvals.",
  },
  {
    logo_images: "../assets/icons/terraform/service-5.png",
    title: "Workspaces",
    description:
      "Structure Terraform workspaces and environments to isolate dev/stage/prod, enforce naming conventions, and manage environment-specific variables securely.",
  },
];

const benefits = [
  "Consistent, repeatable infrastructure deployments",
  "Reduced manual errors and configuration drift",
  "Faster provisioning across multi-cloud environments",
  "Built-in change review via Terraform plans",
  "Policy enforcement and compliance with guardrails",
  "Improved collaboration between DevOps and product teams",
];

const expertise = [
  "HashiCorp Certified Terraform Associates and multi-cloud architects",
  "Terraform Cloud/Enterprise automation with Sentinel policy-as-code",
  "GitOps workflows combining Terraform, ArgoCD, and Vault",
  "Modular design patterns for VPCs, AKS/EKS clusters, and shared services",
  "Zero-downtime infrastructure updates with blue/green strategies",
  "Cost optimization and tagging strategies baked into IaC",
];

const useCases = [
  {
    title: "Global Landing Zone Automation",
    description:
      "Built Terraform modules for AWS multi-account landing zones with guardrails, IAM controls, and networking, reducing provisioning time from weeks to hours.",
  },
  {
    title: "Multi-Cloud Platform Rollout",
    description:
      "Implemented Terraform automation for Azure and GCP environments with centralized state and CI/CD pipelines, enabling consistent deployments across 30+ regions.",
  },
  {
    title: "Regulated Infrastructure Compliance",
    description:
      "Integrated Terraform with Sentinel policies and OPA to enforce HIPAA and SOC 2 controls, delivering automated compliance evidence for every change.",
  },
  {
    title: "Developer Self-Service Portals",
    description:
      "Created Terraform module catalogs exposed via ServiceNow and Backstage, allowing teams to provision approved infrastructure with single-click workflows.",
  },
];

const reasons = [
  "Deep Terraform expertise with production-scale implementations",
  "Battle-tested module libraries and accelerators",
  "Security-first approach with Vault, KMS, and policy-as-code",
  "Automation across CI/CD, GitOps, and Terraform Cloud",
  "Multi-cloud experience in AWS, Azure, GCP, and hybrid setups",
  "24/7 support for critical infrastructure deployments",
];

const scrollToContact = () => {
  document
    .getElementById("contact-section")
    ?.scrollIntoView({ behavior: "smooth" });
};

const TerraformAutomation = () => {
  return (
    <>
      <Helmet>
        <meta
          name="description"
          content="Automate your cloud infrastructure with expert Terraform consulting. Secure, scalable, and cost-effective solutions for your business. Get a free quote today!"
        />
        <meta name="copyright" content="codeglim" />
        <meta
          property="og:title"
          content="Expert Terraform Consulting Services | Secure & Scale Your Cloud | Ayanshtech Solutions"
        />
        <meta property="og:site_name" content="Ayanshtech Solutions" />
        <meta
          property="og:url"
          content="http://ayanshtechnology.com/devops-terraform"
        />
        <meta
          property="og:description"
          content="Automate your cloud infrastructure with expert Terraform consulting. Secure, scalable, and cost-effective solutions for your business. Get a free quote today!"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@Ayanshtech Solutions" />
        <meta
          name="twitter:title"
          content="Expert Terraform Consulting Services | Secure & Scale Your Cloud | Ayanshtech Solutions"
        />
        <meta
          name="twitter:description"
          content="Automate your cloud infrastructure with expert Terraform consulting. Secure, scalable, and cost-effective solutions for your business. Get a free quote today!"
        />
        <meta
          name="twitter:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <link
          rel="canonical"
          href="http://ayanshtechnology.com/devops-terraform"
        />
        <title>
          Expert Terraform Consulting Services | Secure & Scale Your Cloud | Ayanshtech Solutions
        </title>
      </Helmet>

      <div className="flex flex-col items-center justify-center mb-6 p-6">
        {/* Hero */}
        <div className="py-5 w-full">
          <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
            <div className="lg:w-2/3 lg:text-left p-4">
              <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                Terraform Automation
              </h1>
              <p className="sm:text-base md:text-lg lg:text-lg mb-3">
                Build infrastructure the same way you ship software. Ayanshtech
                automates cloud provisioning using Terraform, delivering
                consistent, secure, and auditable infrastructure across every
                environment.
              </p>
              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                From module design and remote state to CI/CD integrations and
                policy-as-code, we help platform teams modernize infrastructure
                operations and embrace DevOps best practices.
              </p>
              <button
                onClick={scrollToContact}
                className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
              >
                Automate with Terraform
              </button>
            </div>
            <div className="flex w-full md:w-1/2 lg:w-[40%] justify-center mb-12 lg:mb-0">
              <img
                src="../assets/images/devops-pages/Terraform-banner.png"
                alt="Terraform Automation"
                className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* About IaC */}
        {/* <div className="mb-5 mt-10 p-4">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Infrastructure as Code (IaC)
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            IaC treats infrastructure definitions as version-controlled code,
            enabling reproducibility, peer review, and automated provisioning. With
            Terraform, teams codify cloud resources, eliminate drift, and enforce
            compliance through automated workflows.
          </p>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Ayanshtech helps organizations adopt IaC at scale—standardizing
            environments, reducing manual toil, and accelerating cloud programs
            across multi-cloud and hybrid ecosystems.
          </p>
        </div> */}

        {/* Services */}
        <div className="text-center mb-5 mt-12">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Terraform Services
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Comprehensive Terraform offerings covering every stage of the IaC
            lifecycle—from module creation to enterprise-scale automation.
          </p>
        </div>
        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <div
                  key={service.title}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"

                >
                  <div className="flex items-center gap-2">
                    <div className="w-[60px] h-[60px]  ">
                      <img src={service.logo_images} className="w-full" alt="" />
                    </div>
                  <h3 className="text-lg font-bold">{service.title}</h3>
                  </div>
                  <p className="text-lg text-gray-700">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Benefits of Terraform
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Terraform unlocks predictable, versioned infrastructure delivery in any
            cloud.
          </p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
              >
                <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Expertise */}
        <div className="p-6 sm:px-8 md:px-12 lg:px-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Our IaC Expertise
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              Terraform specialists with proven track records in regulated,
              high-scale environments.
            </p>
          </div>
          <div className="flex justify-center flex-col md:flex-row item-center gap-4 ">
          <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-7xl my-auto space-y-3">
            {expertise.map((item) => (
              <li key={item} className="text-gray-700">
                {item}
              </li>
            ))}
          </ul>
          <div className="w-full md:w-1/3 lg:w-[40%]">
            <img src="../assets/images/devops-pages/Terraform-image.png" alt="" />
          </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="text-center mb-5 mt-10">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Real-World Use Cases
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Success stories from enterprises embracing Terraform automation.
          </p>
        </div>
        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {useCases.map((useCase) => (
                <div
                  key={useCase.title}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"

                >
                  <h3 className="text-lg font-bold text-gray-900">
                    {useCase.title}
                  </h3>
                  <p className="text-lg text-gray-700">
                    {useCase.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Why Choose Ayanshtech for Terraform
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              Trusted partners for enterprise IaC programs and automation at scale.
            </p>
          </div>
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl mx-auto">
              {reasons.map((reason) => (
                <div
                  key={reason}
                  className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                >
                  <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
                  <p className="text-lg text-gray-700">{reason}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center">
            <button
              onClick={scrollToContact}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
            >
              Schedule a Terraform Strategy Session
            </button>
          </div>
        </div>

        {/* CTA */}
        <div id="contact-section" className="w-full">
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default TerraformAutomation;

